import type { MutationResult } from '@apollo/react-common';
import { useMutation } from '@apollo/react-hooks';
import gqlTagSwag from 'graphql-tag';
import {
	useEpicCardsState,
	useEpicsStoreActions,
} from '@atlassian/jira-backlog-epics-store/src/selectors/index.tsx';
import type { RankEpicsMutationResponse, RankEpics } from './types.tsx';

export const RANK_EPIC_MUTATION = gqlTagSwag`
    mutation SoftwareEpicPanelRank(
        $boardId: ID!
        $issueParentIds: [ID!]!
        $rankBeforeIssueParentId: Long
        $rankAfterIssueParentId: Long
    ) {
        rankIssueParent(
            input: {
                boardId: $boardId
                issueParentIds: $issueParentIds
                rankBeforeIssueParentId: $rankBeforeIssueParentId
                rankAfterIssueParentId: $rankAfterIssueParentId
            }
        ) {
            statusCode
            success
            message
        }
    }
`;

const getRankBeforeAfterIssueParentId = (
	cards: {
		epic: {
			id: number;
		};
	}[],
	index: number,
):
	| {
			rankAfterIssueParentId: number;
	  }
	| {
			rankBeforeIssueParentId: number;
	  } // eslint-disable-next-line @typescript-eslint/no-explicit-any
	| Record<any, any> => {
	if (cards.length === 0) {
		// Unable to return just an empty plain object {}
		// https://github.com/facebook/flow/issues/2977
		return Object.freeze({});
	}

	// last one ?
	if (index === cards.length) {
		return { rankAfterIssueParentId: cards[index - 1].epic.id };
	}
	return { rankBeforeIssueParentId: cards[index].epic.id };
};

export const useRankEpicsMutation = (
	boardId: number,
): [RankEpics, MutationResult<RankEpicsMutationResponse>] => {
	const [mutate, epicRankingMutationData] = useMutation(RANK_EPIC_MUTATION);
	const [{ cards }] = useEpicCardsState();

	const [, { rankEpics: rankEpicsAction }] = useEpicsStoreActions();

	const rankEpics: RankEpics = (issueParentIds: number[], newPosition: number) =>
		mutate({
			variables: {
				boardId,
				issueParentIds,
				...getRankBeforeAfterIssueParentId(
					cards.filter(({ epic: { id } }) => !issueParentIds.includes(id)),
					newPosition,
				),
			},

			update: () => {
				rankEpicsAction(issueParentIds, newPosition);
			},
			optimisticResponse: {
				rankIssueParent: {
					__typename: 'GenericMutationResponse',
					statusCode: 200,
					success: true,
					message: '',
				},
			},
		});

	return [rankEpics, epicRankingMutationData];
};
